import type { LotExpiry, MovementKind, StockLevel, WasteReason } from './api';

/**
 * Cómo se escriben los números en Perú: miles con punto, decimales con coma.
 *
 * La API los manda al revés —`"4780.15"`, con punto decimal— porque así viaja el
 * dinero en todo el producto y así los entrega PostgreSQL. La vuelta se da aquí,
 * en el último sitio, y no antes: un número que ya se convirtió para enseñarlo
 * no se puede volver a sumar sin equivocarse.
 */

const formateador = new Intl.NumberFormat('es-PE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `S/ 4.780,15`. El símbolo va delante y con un espacio, como en la boleta. */
export function soles(value: string | number): string {
  const numero = typeof value === 'string' ? Number.parseFloat(value) : value;

  return `S/ ${formateador.format(Number.isNaN(numero) ? 0 : numero)}`;
}

/** `S/ 4.780`, sin decimales. Para las cifras grandes de la portada. */
export function solesRedondos(value: string | number): string {
  const numero = typeof value === 'string' ? Number.parseFloat(value) : value;

  return `S/ ${new Intl.NumberFormat('es-PE', {
    maximumFractionDigits: 0,
  }).format(Number.isNaN(numero) ? 0 : numero)}`;
}

/**
 * `41,2 %`. Con coma, como el resto de los números.
 *
 * Existe porque `toFixed` escribe con punto, y en la misma frase donde el
 * costo sale `S/ 22,34` un `41.2 %` se lee como si viniera de otro sistema.
 */
export function porcentaje(value: number, decimales = 1): string {
  return `${puntos(value, decimales)} %`;
}

/**
 * `1 ingrediente` y `3 ingredientes`, con el número delante.
 *
 * Estaba escrito a mano con un ternario en cinco sitios, y en el sexto —el
 * botón de guardar una receta— se olvidó: decía «Guardar con 1 ingredientes».
 * El plural del español es regular aquí, así que basta una función.
 */
export function contados(cuantos: number, singular: string, plural: string): string {
  return `${cuantos} ${cuantos === 1 ? singular : plural}`;
}

/** El mismo número sin el signo: «11,2 puntos sobre la meta». */
export function puntos(value: number, decimales = 1): string {
  return new Intl.NumberFormat('es-PE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(Number.isFinite(value) ? value : 0);
}

/**
 * `8,4` y `22`, no `8,400` ni `22,000`.
 *
 * Los ceros de la derecha sobran en pantalla: el almacén cuenta 22 unidades, no
 * 22,000. Se guardan tres decimales porque hacen falta para no perder gramos al
 * sumar; enseñarlos convertiría cada tabla en una pared de ceros.
 */
export function cantidad(value: string): string {
  const numero = Number.parseFloat(value);
  if (Number.isNaN(numero)) return value;

  return new Intl.NumberFormat('es-PE', {
    maximumFractionDigits: 3,
  }).format(numero);
}

/** `+9,06` / `−0,66`. El signo delante, que es lo que lee el kardex. */
export function cantidadConSigno(value: string): string {
  const numero = Number.parseFloat(value);

  return `${numero > 0 ? '+' : ''}${cantidad(value)}`;
}

/** `31 ago` · `31 ago 2026` si es de otro año. */
export function dia(iso: string): string {
  const fecha = new Date(iso);
  const esteAno = fecha.getFullYear() === new Date().getFullYear();

  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit',
    month: 'short',
    ...(esteAno ? {} : { year: 'numeric' }),
    timeZone: 'America/Lima',
  })
    .format(fecha)
    .replace('.', '');
}

/**
 * `14:22`, en hora del local.
 *
 * Con la zona horaria puesta a mano y no la del navegador. El dueño que mira
 * esto desde Madrid tiene que ver la hora a la que pasó **en el restaurante**,
 * no la que era en su reloj.
 */
export function hora(iso: string): string {
  return new Intl.DateTimeFormat('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Lima',
  }).format(new Date(iso));
}

// ── Los días del restaurante ──────────────────────────────────────────────

const DIA_DE_LIMA = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Lima',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * `2026-09-14`: el día **en Lima**, no el de UTC.
 *
 * `toISOString().slice(0, 10)` es el día de Greenwich, y a las 19:00 de Lima
 * ya es mañana: el informe de «hoy» pedido de noche preguntaba por un día que
 * aún no había empezado, y el nombre del conteo llevaba la fecha siguiente.
 */
export function diaDeLima(instante: Date = new Date()): string {
  return DIA_DE_LIMA.format(instante);
}

/** `2026-09-14` + 3 → `2026-09-17`. Con días de calendario, sin horas. */
export function sumarDias(dia: string, dias: number): string {
  const fecha = new Date(`${dia}T12:00:00Z`);
  fecha.setUTCDate(fecha.getUTCDate() + dias);
  return fecha.toISOString().slice(0, 10);
}

/** El primer y el último día del mes `2026-09`. */
export function limitesDelMes(mes: string): { desde: string; hasta: string } {
  const [ano, numero] = mes.split('-').map(Number);
  const ultimo = new Date(Date.UTC(ano!, numero!, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

// ── El vocabulario ────────────────────────────────────────────────────────

export const NIVEL: Record<StockLevel, { texto: string; tono: Tono }> = {
  ok: { texto: 'En rango', tono: 'exito' },
  low: { texto: 'Bajo mínimo', tono: 'aviso' },
  critical: { texto: 'Crítico', tono: 'peligro' },
  out: { texto: 'Agotado', tono: 'peligro' },
  over: { texto: 'Sobrestock', tono: 'marca' },
};

export const VENCIMIENTO: Record<LotExpiry, { texto: string; tono: Tono }> = {
  fresh: { texto: 'Vigente', tono: 'exito' },
  expiring: { texto: 'Por vencer', tono: 'aviso' },
  expired: { texto: 'Vencido', tono: 'peligro' },
};

export const MOVIMIENTO: Record<MovementKind, { texto: string; tono: Tono }> = {
  entry: { texto: 'Entrada', tono: 'exito' },
  sale: { texto: 'Salida por venta', tono: 'neutro' },
  production: { texto: 'Producción', tono: 'marca' },
  waste: { texto: 'Merma', tono: 'peligro' },
  transfer: { texto: 'Traspaso', tono: 'aviso' },
  adjustment: { texto: 'Ajuste', tono: 'aviso' },
  return: { texto: 'Devolución', tono: 'aviso' },
  customer_return: { texto: 'Devolución del cliente', tono: 'exito' },
};

export const MOTIVO_DE_MERMA: Record<WasteReason, string> = {
  expired: 'Vencido',
  natural_decay: 'Deterioro natural',
  cold_chain_break: 'Rotura de cadena de frío',
  preparation_error: 'Error de preparación',
  handling_break: 'Rotura en manipulación',
  customer_return: 'Devolución de cliente',
  contamination: 'Plaga o contaminación',
  own_consumption: 'Consumo propio',
  theft: 'Robo o faltante',
  other: 'Otro',
  cancelled_dish: 'Plato anulado',
};

/**
 * Los motivos en el orden en que se buscan, que no es el alfabético.
 *
 * Primero los que más se registran; los dos que piden firma no van al principio
 * a propósito, para que nadie los toque por costumbre.
 */
export const MOTIVOS_EN_ORDEN: WasteReason[] = [
  'expired',
  'natural_decay',
  'cold_chain_break',
  'preparation_error',
  'handling_break',
  'customer_return',
  'contamination',
  'own_consumption',
  'theft',
  'other',
];

/**
 * Los motivos que se ofrecen al registrar una merma en este negocio.
 *
 * «Error de preparación» es de cocina: en una ferretería no se prepara nada, y
 * ofrecerlo es invitar a apuntar ahí lo que fue una rotura. Sin el módulo de
 * cocina no sale; lo ya registrado con él se sigue leyendo igual.
 */
export function motivosDeMerma(conCocina: boolean): WasteReason[] {
  return conCocina
    ? MOTIVOS_EN_ORDEN
    : MOTIVOS_EN_ORDEN.filter((motivo) => motivo !== 'preparation_error');
}

/**
 * Las que acusan a alguien: no las cierra en solitario quien las descubre. El
 * robo entró el 9 de octubre, con la merma rápida del celular.
 */
export function pideFirma(reason: WasteReason): boolean {
  return reason === 'expired' || reason === 'cold_chain_break' || reason === 'theft';
}

export type Tono = 'exito' | 'aviso' | 'peligro' | 'neutro' | 'marca';

/**
 * Las iniciales que se ponen en un avatar redondo.
 *
 * Se saltan las palabras cortas —«de», «del», «la»— porque en «Distribuidora
 * Lima Norte» nadie diría «DLN». Pero si saltárselas dejara una sola letra,
 * como en «Ana Li», se toman las dos primeras palabras tal cual: una inicial
 * suelta dentro de un círculo no identifica a nadie.
 */
export function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter((parte) => parte.length > 0);
  const fuertes = partes.filter((parte) => parte.length > 2);

  return (fuertes.length >= 2 ? fuertes : partes)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? '')
    .join('');
}
